import { useMemo } from "react";
import {
  useSpeakingParticipants,
  useTracks,
  type TrackReferenceOrPlaceholder,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import { CallTile } from "./CallTile";
import { useCallLayoutTier, type CallLayoutTier } from "../hooks/useCallLayoutTier";

/**
 * Grid layout. Phones use `cappedColumns` and stretch tiles to fill leftover
 * height. Desktop matches the design gallery: one centered 16:9 tile when
 * solo, otherwise a 3-column 16:9 track (two people sit in the first two
 * columns with an empty third — not stretched edge-to-edge).
 *
 * (Our own local component — not `@livekit/components-react`'s GridLayout.)
 */
export function GridLayout() {
  const tier = useCallLayoutTier();
  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false },
  );
  const screenTracks = useTracks(
    [{ source: Track.Source.ScreenShare, withPlaceholder: false }],
    { onlySubscribed: false },
  );
  const speakingParticipants = useSpeakingParticipants();
  const speakingSids = useMemo(
    () => new Set(speakingParticipants.map((p) => p.sid)),
    [speakingParticipants],
  );

  // Show screen-share tiles alongside cameras when someone's presenting —
  // the shared screen is more relevant than the presenter's webcam.
  const tracks = useMemo<TrackReferenceOrPlaceholder[]>(
    () => [...screenTracks, ...cameraTracks],
    [screenTracks, cameraTracks],
  );

  const isDesktop = tier === "desktop";
  const cols = isDesktop
    ? tracks.length <= 1
      ? 1
      : 3
    : cappedColumns(Math.max(tracks.length, 1), tier);
  const rows = Math.max(1, Math.ceil(tracks.length / cols));
  const twoUp = !isDesktop && tracks.length === 2;
  // Solo (and 3+) on phones/tablets stretch to fill leftover height. A 2-up
  // row stays pinned to the top at a readable card height — half-width 16:9
  // on a 390px phone is only ~100px tall, which is the postage-stamp look.
  const stretchTiles = !isDesktop && !twoUp;

  const gridClass = isDesktop
    ? "h-full min-h-0 w-full overflow-y-auto no-scrollbar p-4 lg:p-6 grid gap-3 lg:gap-4 content-center auto-rows-min"
    : stretchTiles
      ? "h-full min-h-0 w-full overflow-y-auto no-scrollbar p-3 sm:p-4 grid gap-3"
      : "h-full min-h-0 w-full overflow-y-auto no-scrollbar p-3 sm:p-4 grid gap-3 content-start";

  return (
    <div
      className={gridClass}
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        ...(stretchTiles
          ? { gridTemplateRows: `repeat(${rows}, minmax(140px, 1fr))` }
          : twoUp
            ? { gridTemplateRows: "minmax(180px, 34%)" }
            : {}),
      }}
      data-grid-cols={cols}
    >
      {tracks.map((trackRef) => (
        <CallTile
          key={`${trackRef.participant.sid}-${trackRef.source}`}
          trackRef={trackRef}
          isSpeaking={speakingSids.has(trackRef.participant.sid)}
          size={!isDesktop && tracks.length <= 2 ? "lg" : "md"}
          className={isDesktop ? "aspect-video" : "h-full min-h-0 w-full"}
        />
      ))}
    </div>
  );
}

function columnsForCount(n: number): number {
  if (n <= 1) return 1;
  if (n <= 2) return 2;
  if (n <= 4) return 2;
  if (n <= 9) return 3;
  return 4;
}

/**
 * Width-aware column count: caps the natural count so tiles never get too thin
 * on a narrow/tall window. mobile ≤2, compact ≤3, desktop ≤4 (unchanged).
 */
export function cappedColumns(n: number, tier: CallLayoutTier): number {
  const cap = tier === "mobile" ? 2 : tier === "compact" ? 3 : 4;
  return Math.min(columnsForCount(n), cap);
}
